import {ParseResult} from './markdown';
import {cls} from '../cls';

import css from './markdown.module.css';

type ColumnMeta = {align: string; length: number};

export type TableOptions = {
	/** у таблицы нет шапки: все строки — тело. Так пишется `[table headless]` */
	headless?: boolean;
};

const DICE = /^\s*\d*d\d+\s*$/;

/** пометка колонки, которую заняла соседняя ячейка слева: в разметке это «||» без пробела */
export const TABLE_SPAN = '\u0000span';

type MergedCell = {content: string; index: number; span: number};

/** ячейки строки с учётом объединения: `| Лёгкая броня ||||` — одна ячейка на четыре колонки */
function mergeCells(cells: string[]): MergedCell[] {
	const merged: MergedCell[] = [];
	cells.forEach((cell, index) => {
		const last = merged[merged.length - 1];
		if (cell === TABLE_SPAN && last) last.span += 1;
		else merged.push({content: cell === TABLE_SPAN ? '' : cell, index, span: 1});
	});
	return merged;
}

/** текст ячейки без разметки: `{@dice 1d100, 1d100}` — та же d100, а по ней считается ширина колонки */
function plainText(cell: string) {
	if (cell === TABLE_SPAN) return '';
	return cell.replace(/\{@[\w-]+\s*([^},]*)(?:,\s*([^}]*))?\}/g, (_, argument, text) => text ?? argument).trim();
}

/** колонка кубика: «d100» в шапке, хоть размеченная, хоть нет */
function isDice(head: string[], index: number) {
	return DICE.test(plainText(head[index] || ''));
}

/** колонка «Уровень» и колонка кубика — узкие, у остальных ширина по числу дефисов в разделителе */
function fixedWidth(head: string[], body: string[][], index: number): string | null {
	const level = body.length > 0 && body.every((cells) => /^\s*\d+-й\s*$/.test(plainText(cells[index] || '')));
	if (level || /^\s*Уровень.+$/.test(plainText(head[index] || ''))) return '120px';
	if (isDice(head, index)) return '60px';
	return null;
}

function makeTable(
	rows: ParseResult[],
	meta: ColumnMeta[] | null,
	options: TableOptions,
	/** сколько строк стоит над разделителем «| --- |»: все они — шапка */
	headRows = 1,
): ParseResult | null {
	if (!rows.length) return null;

	const cellsOf = (row: ParseResult) => (row.tmp as string[]) || [];
	const widest = Math.max(...rows.map((row) => cellsOf(row).length), 1);
	// разделителя нет — у таблицы без шапки его и не пишут
	const columns: ColumnMeta[] = meta ?? Array.from({length: widest}, () => ({align: 'Left', length: 1}));
	const count = columns.length;
	const normalize = (cells: string[]) => Array.from({length: count}, (_, index) => cells[index] ?? '');

	const headCount = options.headless ? 0 : Math.max(1, headRows);
	const heads = rows.slice(0, headCount).map((row) => normalize(cellsOf(row)));
	const head = heads[heads.length - 1] || [];
	const body = rows.slice(headCount).map((row) => normalize(cellsOf(row)));
	// пустая шапка старого формата — «| | |» над разделителем — тоже значит, что шапки нет
	const hasHead = heads.some((cells) => cells.some((cell) => cell !== TABLE_SPAN && cell.trim()));

	const fixed = columns.map((_, index) => fixedWidth(head, body, index));
	const units = columns.reduce((sum, column, index) => (fixed[index] ? sum : sum + column.length), 0) || 1;
	// доли колонок задаются, только пока в таблице нет узких колонок с шириной в пикселях
	const proportional = fixed.every((width) => !width);
	const widthOf = (index: number) =>
		fixed[index] || (proportional ? `${Math.round((columns[index].length / units) * 1000) / 10}%` : undefined);

	// бросок в шапке — числа в колонке короткие, по центру они читаются лучше
	const align = (index: number) => (isDice(head, index) ? css.alignCenter : css[`align${columns[index].align}`]);

	return {
		tag: 'div',
		props: {className: css.tableScroll},
		children: [
			{
				tag: 'table',
				props: {className: css.table},
				children: [
					{
						tag: 'colgroup',
						children: columns.map((_, index) => ({tag: 'col', props: {style: {width: widthOf(index)}}})),
					},
					...(hasHead
						? [
								{
									tag: 'thead',
									children: heads.map((cells) => ({
										tag: 'tr',
										children: mergeCells(cells).map(({content, index, span}) => ({
											tag: 'th',
											content,
											props: {
												scope: span > 1 ? 'colgroup' : 'col',
												colSpan: span > 1 ? span : undefined,
												className: span > 1 ? css.alignCenter : align(index),
											},
										})),
									})),
								},
							]
						: []),
					{
						tag: 'tbody',
						children: body.map((cells) => ({
							tag: 'tr',
							children: mergeCells(cells).map(({content, index, span}) => ({
								tag: 'td',
								content,
								props: {
									colSpan: span > 1 ? span : undefined,
									className: cls(align(index), span === count && css.tableGroup),
								},
							})),
						})),
					},
				],
			},
		],
	} as ParseResult;
}

export function wrapTable(tree: ParseResult[], options: TableOptions = {}) {
	const result: ParseResult[] = [];
	let tableItems: ParseResult[] = [];
	let meta: ColumnMeta[] | null = null;
	let headRows = 1;

	for (let i = 0; i < tree.length; i++) {
		const item = tree[i];
		if (item.tag === 'tableMeta') {
			meta = item.tmp;
			headRows = tableItems.length;
		} else if (item.tag === 'tableRow') {
			tableItems.push(item);
		}
		if (!['tableRow', 'tableMeta'].includes(item.tag) || i === tree.length - 1) {
			if (tableItems.length) {
				const tableResult = makeTable(tableItems, meta, options, meta ? headRows : 1);
				if (tableResult) result.push(tableResult);
				tableItems = [];
				meta = null;
				headRows = 1;
			}
			if (!['tableRow', 'tableMeta'].includes(item.tag)) result.push(item);
		}
	}
	return result;
}
